<script setup>
import { ref, defineProps, onBeforeMount, onUnmounted, onMounted, nextTick } from 'vue';
import { isEmpty } from '@/common/utils/Util';
import store from '@/store';
import InputComponent from '@/components/common/input/InputComponent.vue';
import { modalList, show, showTextModal } from '@/common/utils/ModalState';

onBeforeMount(() => {
  store.commit('layout/setIsHeader', false);
  store.commit('business/addressSearchText');
});

onMounted(() => {
  nextTick(() => {
    naverSearchInput.value?.focus();
  });
});

onUnmounted(() => {
  if (isEmpty(closeHeader) || !closeHeader) {
    store.commit('layout/setIsHeader', true);
  }
});

const emits = defineEmits(['close']);
const keyword = ref('');
const items = ref([]);
const naverSearchInput = ref();
const beforeSearch = ref(true);
const searchType = ref('주소');

const props = defineProps(['data']);
const { fnCallBack, unClose, closeHeader, isDirectInput, overseas, interceptKorean } = props.data;

// 260702: 해외+interceptKorean 모드에서 한국 주소 감지 시 국내 주소검색으로 전환.
//   안내 모달 → switchToDomestic 플래그로 부모(registerNew)에 위임(부모가 isGlobal=false + 카카오 우편번호 검색 진입).
//   입력단(한글 키워드)·결과단(item.countryCode==='kr') 공통 사용.
const KOREAN_REGEX = /[ㄱ-ㅎㅏ-ㅣ가-힣]/;
const interceptToDomestic = () => {
  showTextModal(
    '입력하신 주소는 한국 주소입니다.\n정확한 위치를 위해 국내 주소 검색으로 전환합니다.',
    '확인',
    () => {
      if (fnCallBack) fnCallBack({ switchToDomestic: true });
      close();
    }
  );
};

const fnShowNaverSearch = async () => {
  if (isEmpty(keyword.value)) return;

  // 260702: 해외+interceptKorean인데 한글 입력이면 OSM(한국 도로명 인식률 낮아 대부분 0건) 건너뛰고 즉시 국내 전환.
  //   기존엔 OSM이 kr 결과를 반환해 fnSelect에서 선택해야만 전환돼, 한글 입력 대부분이 "아무 반응 없음"이던 문제(인식률↓) 보완.
  if (overseas && interceptKorean && KOREAN_REGEX.test(keyword.value)) {
    interceptToDomestic();
    return;
  }

  // 260527: 해외는 OSM 지오코딩(백엔드 경유), 국내는 네이버 지역검색. 응답 형식 동일.
  let response = await store.dispatch(
    overseas ? 'common/fetchOverseasGeocode' : 'common/fetchNaverLocalData',
    keyword.value
  );
  beforeSearch.value = false;
  items.value = response.items || [];
  // store.commit('business/addressSearchText', keyword.value);
};

const close = () => {
  setTimeout(() => {
    emits('close');
  }, 200);
};

const isDirect = () => {
  if (fnCallBack) fnCallBack({ isDirectInput: isDirectInput });
  setTimeout(() => {
    emits('close');
  }, 200);
};

const formatTitle = title => {
  // title의 첫 글자가 `<b>로 시작하면 <b> 태그 제거, 아니면 ' '으로 대체
  return title.replace(/<b>/gi, '').replace(/<\/b>/gi, '');
};

const fnSelect = item => {
  // 260527: 해외 검색 결과가 한국 주소(country_code=kr)면 국내 주소검색으로 전환(입력단 한글 감지와 공통 경로).
  //   부모가 interceptKorean=true로 연 경우에만 동작(등록=registerNew).
  if (overseas && interceptKorean && item.countryCode === 'kr') {
    interceptToDomestic();
    return;
  }
  const data = {
    latitude: item.mapy / 10 ** 7,
    longitude: item.mapx / 10 ** 7,
    title: formatTitle(item.title),
    address: item.address,
    roadAddress: item.roadAddress
  };
  if (fnCallBack) fnCallBack(data);
  if (isEmpty(unClose) || !unClose) close();
};
const kakaoModal = () => {
  keyword.value = '';
  if (searchType.value === '주소') {
    const data = {
      fnCallBack: async addressData => {
        const payload = {
          /*
           userSelectedType 는 카카오맵 검색결과 리스트에서 선택한 주소 타입을 의미
           R: 도로명 주소,
           J: 지번 주소
           */
          userSelectedType: addressData.userSelectedType,
          latitude: addressData.latitude,
          longitude: addressData.longitude,
          address: addressData.jibunAddress || addressData.autoJibunAddress,
          roadAddress: addressData.roadAddress
        };
        if (fnCallBack) fnCallBack(payload);
        if (isEmpty(unClose) || !unClose) close();
      }
    };
    show(modalList.KAKAO_POST, data);
  }
};
</script>

<template>
  <div class="modal-dim on">
    <!--'on 추가'-->

    <div class="modal-wrap">
      <div class="modal-inner whole">
        <div class="modal bg-gray">
          <header class="header">
            <div class="inner">
              <h1 class="ttl">{{ overseas ? '해외 주소 검색' : '주소 검색' }}</h1>
              <div class="right">
                <button class="header-btn" @click="close"><i class="ico-close-gray90"></i></button>
              </div>
            </div>
          </header>

          <div class="head">
            <div class="search-inner">
              <label class="label  mg0" v-if="!overseas">
                <select v-model="searchType" @change="kakaoModal">
                  <option value="주소">주소</option>
                  <option value="건물">건물</option>
                </select>
              </label>
              <label class="label search ml10">
                <input
                  type="text"
                  :placeholder="overseas ? '해외 주소·장소명을 입력하세요 (예: Auckland CBD)' : (searchType === '주소' ? '도로명/지번으로 검색해주세요' : '건물명으로 검색해주세요')"
                  v-model="keyword"
                  @keyup.enter="fnShowNaverSearch"
                  ref="naverSearchInput"
                  @click="!overseas && searchType === '주소' ? kakaoModal() : null"
                />
                <button class="btn-search" @click="fnShowNaverSearch"><i class="ico-search"></i></button>
              </label>
            </div>
          </div>
          <!-- Items -->

          <div class="modal-con" v-if="!beforeSearch && items.length === 0">
            <div class="empty-wrap">
              <div class="empty">
                <div>조건에 맞는 결과가 없습니다.</div>
                <button
                  class="btn border-gray small"
                  @click="isDirect"
                  v-if="!isEmpty(isDirectInput) && isDirectInput === true"
                >
                  직접 입력하기
                </button>
              </div>
            </div>
          </div>

          <div class="modal-con pd16" v-else>
            <!--<div class="modal-con pd16" v-if="items.length > 0 && !beforeSearch">-->
            <ul class="card-list pd0">
              <!-- departed: 고인목록 | church: 교회목록 | onlineFuneral: 온라인장례식목록-->

              <li class="card address-list" v-for="(item, index) in items" :key="index" @click="fnSelect(item)">
                <div class="top flex-justi-con-space-between">
                  <p class="name">{{ formatTitle(item.title) }}</p>
                  <span class="sm-txt">{{ item.category }}</span>
                </div>
                <div class="bottom">
                  <p class="address" v-if="overseas">{{ item.address }}</p>
                  <template v-else>
                    <p class="address">{{ '도로명 : ' + item.roadAddress }}</p>
                    <p class="address">{{ '지번 : ' + item.address }}</p>
                  </template>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.church-info {
  width: 100%;
}
</style>
